import { useState, useEffect } from "react";
import { useAuth, apiClient } from "@/App";
import { toast } from "sonner";
import {
  Users,
  Plus,
  Search,
  Mail,
  Phone,
  Building2,
  Edit2,
  Trash2,
  UserPlus,
  Tag
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

export default function Contacts() {
  const { isFounder } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    category: "team",
    company: "",
    role: "",
    notes: ""
  });

  const categories = [
    { value: "team", label: "Team Member", color: "bg-blue-500/20 text-blue-400" },
    { value: "staff", label: "Staff", color: "bg-green-500/20 text-green-400" },
    { value: "vendor", label: "Vendor", color: "bg-purple-500/20 text-purple-400" },
    { value: "contractor", label: "Contractor", color: "bg-orange-500/20 text-orange-400" },
    { value: "consultant", label: "Consultant", color: "bg-teal-500/20 text-teal-400" },
    { value: "other", label: "Other", color: "bg-gray-500/20 text-gray-400" }
  ];

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await apiClient.get("/contacts");
      setContacts(response.data);
    } catch (error) {
      console.error("Failed to fetch contacts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      if (editingContact) {
        await apiClient.put(`/contacts/${editingContact.contact_id}`, formData);
        toast.success("Contact updated!");
      } else {
        await apiClient.post("/contacts", formData);
        toast.success("Contact added!");
      }
      setShowModal(false);
      resetForm();
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save contact");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setFormData({
      name: contact.name,
      email: contact.email,
      phone: contact.phone || "",
      category: contact.category,
      company: contact.company || "",
      role: contact.role || "",
      notes: contact.notes || ""
    });
    setShowModal(true);
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm("Delete this contact?")) return;
    
    try {
      await apiClient.delete(`/contacts/${contactId}`);
      toast.success("Contact deleted");
      fetchContacts();
    } catch (error) {
      toast.error("Failed to delete contact");
    }
  };

  const resetForm = () => {
    setEditingContact(null);
    setFormData({
      name: "",
      email: "",
      phone: "",
      category: "team",
      company: "",
      role: "",
      notes: ""
    });
  };

  const getCategoryStyle = (category) => {
    return categories.find(c => c.value === category)?.color || "bg-gray-500/20 text-gray-400";
  };

  const getCategoryLabel = (category) => {
    return categories.find(c => c.value === category)?.label || category;
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         contact.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         contact.company?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === "all" || contact.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Group contacts by category
  const groupedContacts = filteredContacts.reduce((acc, contact) => {
    const cat = contact.category || "other";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(contact);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="page-content">
        <div className="space-y-4">
          <div className="h-8 skeleton w-1/4"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (<div key={i} className="h-24 skeleton"></div>))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content animate-fade-in" data-testid="contacts-page">
      {/* Header */}
      <div className="page-header flex-col sm:flex-row gap-3 sm:gap-0">
        <div>
          <h1 className="page-title text-lg sm:text-xl lg:text-2xl">Contacts</h1>
          <p className="page-subtitle text-[10px] sm:text-xs lg:text-sm">Team, staff, vendors & more</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-[#FF4500] hover:bg-[#E65100] text-[10px] sm:text-xs h-8 sm:h-9" data-testid="add-contact-btn">
          <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Add Contact
        </Button>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
          <Input
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 bg-[#1A1A1A] border-[#262626] h-8 sm:h-9 text-[11px] sm:text-xs"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-36 bg-[#1A1A1A] border-[#262626] h-8 sm:h-9 text-[11px] sm:text-xs">
            <Tag className="w-3 h-3 mr-1.5" />
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-[#262626]">
            <SelectItem value="all" className="text-[11px] sm:text-xs">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.value} value={cat.value} className="text-[11px] sm:text-xs">{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div className="bg-[#1A1A1A] border border-[#262626] rounded p-2.5 sm:p-3 text-center">
          <p className="text-lg sm:text-xl font-bold text-white">{contacts.length}</p>
          <p className="text-[9px] sm:text-[10px] text-gray-500 uppercase tracking-wider">Total</p>
        </div>
        {categories.slice(0, 5).map(cat => (
          <div key={cat.value} className="bg-[#1A1A1A] border border-[#262626] rounded p-2.5 sm:p-3 text-center">
            <p className="text-lg sm:text-xl font-bold text-white">{contacts.filter(c => c.category === cat.value).length}</p>
            <p className="text-[9px] sm:text-[10px] text-gray-500 uppercase tracking-wider">{cat.label.split(' ')[0]}</p>
          </div>
        ))}
      </div>

      {/* Contacts List */}
      {filteredContacts.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500 text-xs sm:text-sm">No contacts found</p>
          <Button onClick={() => { resetForm(); setShowModal(true); }} variant="link" className="text-[#FF4500] text-xs mt-2">
            Add your first contact
          </Button>
        </div>
      ) : (
        <div className="space-y-4 sm:space-y-6">
          {Object.entries(groupedContacts).map(([category, categoryContacts]) => (
            <div key={category} className="animate-fade-in">
              <div className="flex items-center gap-2 mb-2 sm:mb-3">
                <span className={`text-[9px] sm:text-[10px] px-2 py-0.5 rounded ${getCategoryStyle(category)}`}>
                  {getCategoryLabel(category)}
                </span>
                <span className="text-[10px] sm:text-xs text-gray-500">{categoryContacts.length}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                {categoryContacts.map((contact) => (
                  <div
                    key={contact.contact_id}
                    className="bg-[#1A1A1A] border border-[#262626] rounded-lg p-3 sm:p-4 hover:border-[#FF4500]/30 transition-all duration-300 group"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 sm:gap-2.5">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gradient-to-br from-[#FF4500]/20 to-[#FF4500]/5 flex items-center justify-center">
                          <span className="text-[11px] sm:text-xs font-medium text-[#FF4500]">
                            {contact.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-white text-[11px] sm:text-xs font-medium">{contact.name}</p>
                          {contact.role && <p className="text-gray-500 text-[9px] sm:text-[10px]">{contact.role}</p>}
                        </div>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => handleEdit(contact)} className="p-1.5 hover:bg-white/5 rounded text-gray-400 hover:text-white transition-colors">
                          <Edit2 className="w-3 h-3" />
                        </button>
                        <button onClick={() => handleDelete(contact.contact_id)} className="p-1.5 hover:bg-red-500/10 rounded text-gray-400 hover:text-red-400 transition-colors">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="space-y-1.5">
                      <a href={`mailto:${contact.email}`} className="flex items-center gap-1.5 text-gray-400 hover:text-[#FF4500] transition-colors">
                        <Mail className="w-3 h-3" />
                        <span className="text-[10px] sm:text-[11px] truncate">{contact.email}</span>
                      </a>
                      {contact.phone && (
                        <a href={`tel:${contact.phone}`} className="flex items-center gap-1.5 text-gray-400 hover:text-[#FF4500] transition-colors">
                          <Phone className="w-3 h-3" />
                          <span className="text-[10px] sm:text-[11px]">{contact.phone}</span>
                        </a>
                      )}
                      {contact.company && (
                        <div className="flex items-center gap-1.5 text-gray-500">
                          <Building2 className="w-3 h-3" />
                          <span className="text-[10px] sm:text-[11px]">{contact.company}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#1A1A1A] border-[#262626] max-w-md mx-4 sm:mx-auto" aria-describedby="contact-form-description">
          <DialogHeader>
            <DialogTitle className="text-white text-sm sm:text-base">{editingContact ? "Edit Contact" : "Add Contact"}</DialogTitle>
            <p id="contact-form-description" className="text-[10px] sm:text-xs text-gray-500">
              {editingContact ? "Update contact details" : "Add a new team member, vendor, or contact"}
            </p>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-gray-400 text-[10px] sm:text-xs">Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="bg-[#171717] border-[#262626] h-8 sm:h-9 text-[11px] sm:text-xs"
                  placeholder="Full name"
                  required
                />
              </div>
              <div className="col-span-2">
                <Label className="text-gray-400 text-[10px] sm:text-xs">Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="bg-[#171717] border-[#262626] h-8 sm:h-9 text-[11px] sm:text-xs"
                  placeholder="email@example.com"
                  required
                />
              </div>
              <div>
                <Label className="text-gray-400 text-[10px] sm:text-xs">Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="bg-[#171717] border-[#262626] h-8 sm:h-9 text-[11px] sm:text-xs"
                  placeholder="+91 98765 43210"
                />
              </div>
              <div>
                <Label className="text-gray-400 text-[10px] sm:text-xs">Category *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger className="bg-[#171717] border-[#262626] h-8 sm:h-9 text-[11px] sm:text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value} className="text-[11px] sm:text-xs">{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-400 text-[10px] sm:text-xs">Company</Label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  className="bg-[#171717] border-[#262626] h-8 sm:h-9 text-[11px] sm:text-xs"
                  placeholder="Company name"
                />
              </div>
              <div>
                <Label className="text-gray-400 text-[10px] sm:text-xs">Role / Title</Label>
                <Input
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="bg-[#171717] border-[#262626] h-8 sm:h-9 text-[11px] sm:text-xs"
                  placeholder="e.g., Architect"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-gray-400 text-[10px] sm:text-xs">Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="bg-[#171717] border-[#262626] text-[11px] sm:text-xs min-h-[60px]"
                  placeholder="Any additional notes..."
                />
              </div>
            </div>
            
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="border-[#262626] text-[10px] sm:text-xs h-8 sm:h-9">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-[#FF4500] hover:bg-[#E65100] text-[10px] sm:text-xs h-8 sm:h-9">
                {submitting ? "Saving..." : editingContact ? "Update" : "Add Contact"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
